export interface MenuItemTypes {
    key: string;
    label: string;
    isTitle?: boolean;
    icon?: string;
    url?: string;
    badge?: {
        variant: string;
        text: string;
    };
    parentKey?: string;
    target?: string;
    permissions?: string[];
    children?: MenuItemTypes[];
}

/*
 * ─── OLD MENU STRUCTURE (preserved for reference) ───────────────────
 *
 * const OLD_MENU_ITEMS: MenuItemTypes[] = [
 *   Navigation: Dashboard
 *   Authentication: Users, User Types, Roles, Permissions
 *   Management: Approvals, Agent Requests (commented), Requests
 *   Agents: Agents, Agent Groups
 *   Suppliers: Suppliers, Waiting Approvals, Invoices (commented)
 *   Customers: Customers, Requests & Sales (commented)
 *   Events: Event Categories, Events
 *   Recommended Content: Recommended Content
 *   Integrations: Staging Queue
 *   (cont.) Categories, Tags, Badges
 *   Vendors: Vendors, Invoices
 *   (orphaned) Payment Intents, Public Assets
 *   Tickets: Tickets
 *   Parameters: Places, Infos, Cars, Likes, Languages, Allergens,
 *     Currencies, FX Rates, Transportation Types, Payment Methods,
 *     Mobile Page Definitions, System Features, Content Components,
 *     Component Templates
 *   Aviation: Jet Classes, Aircrafts, Empty Legs
 *   Yachts: Yachts, Cars, Yacht Types
 *   (orphaned) Vendor Payment Methods
 * ];
 */

const MENU_ITEMS: MenuItemTypes[] = [
    // ─── MAIN ───────────────────────────────────────────────────────
    {key: "main", label: "Main", isTitle: true},
    {
        key: "dashboard",
        label: "Dashboard",
        isTitle: false,
        icon: "airplay",
        url: "/dashboard",
    },

    // ─── OPERATIONS ─────────────────────────────────────────────────
    {
        key: "operations",
        label: "Operations",
        permissions: ["see_roles", "see_users", "see_permissions"],
        isTitle: true,
    },
    {
        key: "management-requests",
        label: "Requests",
        isTitle: false,
        icon: "inbox",
        permissions: ["see_roles"],
        url: "/management/requests",
    },
    // "Approvals" hidden from the admin portal nav (D3 — route stays live).
    // Restore this entry to bring it back.
    // {
    //     key: "management-approvals",
    //     label: "Approvals",
    //     isTitle: false,
    //     icon: "check-square",
    //     permissions: ["see_users"],
    //     url: "/management/approvals",
    // },
    {
        key: "admin-offer-transfers",
        label: "Offer Transfers",
        isTitle: false,
        icon: "repeat",
        permissions: ["admin_manage_offers"],
        url: "/admin/offer-transfers",
    },
    {
        key: "integrations-staging",
        label: "Staging Queue",
        isTitle: false,
        icon: "layers",
        url: "/admin/integrations/staging",
    },
    {
        // Listings submitted for review that are not live yet, across every type.
        key: "inventory-submissions",
        label: "Submissions",
        isTitle: false,
        icon: "inbox",
        permissions: ["admin_moderate_yachts", "admin_moderate_cars", "admin_approve_experiences", "admin_moderate_hotels", "admin_moderate_restaurants", "admin_moderate_events", "admin_manage_aircrafts", "publish_recommended_content"],
        url: "/admin/inventory/submissions",
    },
    {
        // Supplier edits to live listings waiting for review (field-level moderation).
        key: "inventory-pending-changes",
        label: "Pending Changes",
        isTitle: false,
        icon: "check-square",
        permissions: ["admin_moderate_yachts", "admin_moderate_cars", "admin_approve_experiences", "admin_moderate_hotels", "admin_moderate_restaurants", "admin_moderate_events", "admin_manage_aircrafts", "publish_recommended_content"],
        url: "/admin/inventory/pending-changes",
    },

    // ─── FLEET & INVENTORY ──────────────────────────────────────────
    {key: "fleet-title", label: "Fleet & Inventory", isTitle: true},
    {
        key: "fleet",
        label: "Fleet & Inventory",
        isTitle: false,
        icon: "navigation",
        // Supplier-owned inventory ONLY: every item here belongs to a supplier,
        // carries a review status and goes through approve/reject. Platform
        // taxonomy that no supplier owns (jet classes, airports, yacht types,
        // event categories) lives in Reference Data next to Car Brands/Models.
        children: [
            { key: "aviation-aircrafts", label: "Aircrafts", url: "/admin/aircrafts", parentKey: "fleet" },
            { key: "aviation-empty-legs", label: "Empty Legs", url: "/admin/empty-legs", parentKey: "fleet" },
            { key: "yachts-list", label: "Yachts", url: "/admin/yachts", parentKey: "fleet" },
            { key: "experiences-list", label: "Experiences", url: "/admin/experiences", parentKey: "fleet", permissions: ["admin_see_experiences"] },
            { key: "cars", label: "Cars", url: "/admin/cars", parentKey: "fleet" },
            { key: "hotels-list", label: "Hotels", url: "/admin/hotels", parentKey: "fleet" },
            { key: "restaurants-list", label: "Restaurants", url: "/admin/restaurants", parentKey: "fleet" },
            { key: "event-events", label: "Events", url: "/events/list", parentKey: "fleet" },
        ],
    },

    // ─── FINANCE ────────────────────────────────────────────────────
    // Money used to be scattered: Payment Intents in Operations, Vendor
    // Invoices under Vendors, Currencies/FX/Payment Methods in Parameters.
    // One person's job, now one section.
    {key: "finance-title", label: "Finance", isTitle: true},
    {
        key: "admin-finance-overview",
        label: "Overview",
        isTitle: false,
        icon: "bar-chart-2",
        permissions: ["admin_see_finance_overview"],
        url: "/admin/finance/overview",
    },
    {
        key: "admin-payment-intents",
        label: "Payment Intents",
        isTitle: false,
        icon: "credit-card",
        url: "/admin/payment-intents",
    },
    {
        key: "admin-finance-withdrawals",
        label: "Withdrawals",
        isTitle: false,
        icon: "credit-card",
        permissions: ["admin_process_withdrawal"],
        url: "/admin/finance/withdrawals",
    },
    {
        key: "admin-finance-statements",
        label: "Supplier Statements",
        isTitle: false,
        icon: "file-text",
        permissions: ["view_supplier_accounts"],
        url: "/admin/finance/statements",
    },
    {
        // The route existed and worked from the day it shipped, but nothing
        // linked to it — reachable only by typing the URL. It is the only
        // screen that answers "show me the postings behind THIS offer", so it
        // belongs next to the statements rather than in someone's bookmarks.
        key: "admin-finance-ledger",
        label: "Ledger",
        isTitle: false,
        icon: "list",
        permissions: ["view_supplier_accounts"],
        url: "/admin/finance/ledger",
    },
    {
        key: "admin-finance-commission",
        label: "Commission",
        isTitle: false,
        icon: "trending-up",
        permissions: ["admin_see_commission_report"],
        url: "/admin/finance/commission",
    },
    {
        key: "admin-finance-reconciliation",
        label: "Reconciliation",
        isTitle: false,
        icon: "shield",
        permissions: ["admin_see_reconciliation"],
        url: "/admin/finance/reconciliation",
    },
    {
        key: "admin-finance-periods",
        // PRD R19 asks for Director-gated screens to be MARKED, so an Officer
        // sees why an entry is unavailable rather than finding it missing.
        label: "Periods (Director)",
        isTitle: false,
        icon: "calendar",
        permissions: ["admin_close_period"],
        url: "/admin/finance/periods",
    },
    {
        key: "admin-finance-referrals",
        label: "Referrals",
        isTitle: false,
        icon: "share-2",
        permissions: ["admin_see_commission_report"],
        url: "/admin/finance/referrals",
    },
    {
        key: "admin-finance-split",
        label: "Refund Split (Director)",
        isTitle: false,
        icon: "call-split",
        permissions: ["admin_configure_refund_split"],
        url: "/admin/finance/split",
    },
    {
        key: "vendor-invoices",
        label: "Vendor Invoices",
        isTitle: false,
        icon: "credit-card",
        url: "/vendor/invoices",
    },
    {
        key: "parameters-currencies",
        label: "Currencies",
        isTitle: false,
        icon: "credit-card",
        url: "/parameters/currencies",
    },
    {
        key: "parameters-fx-rates",
        label: "FX Rates",
        isTitle: false,
        icon: "repeat",
        url: "/parameters/fx-rates",
    },
    // Catalogue of platform payment methods (D2). Vendor assignment lives
    // under Suppliers as "Vendor Payment Methods".
    {
        key: "parameters-payment-methods",
        label: "Payment Methods",
        isTitle: false,
        icon: "credit-card",
        url: "/parameters/payment-methods",
    },

    // ─── COMMERCIAL POLICY ──────────────────────────────────────────
    // The consequential switches — commission behaviour, whether offers reach
    // customers unreviewed, which suppliers see which requests — pulled out of
    // the lookup-table drawer they were buried in.
    {key: "commercial-policy-title", label: "Commercial Policy", isTitle: true},
    {
        key: "parameters-services",
        label: "Services",
        isTitle: false,
        icon: "sliders",
        url: "/parameters/services",
    },
    {
        key: "parameters-offer-approval",
        label: "Offer Approval",
        isTitle: false,
        icon: "check-square",
        permissions: ["see_offer_approval_policy"],
        url: "/parameters/offer-approval",
    },
    {
        // Verati's commission on requests a supplier creates for itself.
        key: "parameters-supplier-request-pricing",
        label: "Supplier-made Requests",
        isTitle: false,
        icon: "percent",
        permissions: ["admin_see_supplier_request_pricing"],
        url: "/parameters/supplier-request-pricing",
    },
    {
        // Which parts of a supplier edit go live at once and which wait for review.
        key: "parameters-inventory-approval",
        label: "Inventory Approval",
        isTitle: false,
        icon: "sliders",
        permissions: ["admin_manage_moderation_policy"],
        url: "/admin/inventory/moderation-policy",
    },
    {
        key: "parameters-dispatch",
        label: "Dispatch",
        isTitle: false,
        icon: "sliders",
        permissions: ["see_dispatch_config"],
        url: "/parameters/dispatch",
    },
    {
        key: "ai-agent",
        label: "AI Agent",
        isTitle: false,
        icon: "grid",
        url: "/ai-agent",
    },
    {
        key: "parameters-compliance-requirements",
        label: "Compliance Requirements",
        isTitle: false,
        icon: "file",
        url: "/parameters/compliance-requirements",
    },

    // ─── CUSTOMER SUPPORT ───────────────────────────────────────────
    // Help content and how the support AIs and the support chat behave.
    {key: "customer-support-title", label: "Customer Support", isTitle: true},
    {
        key: "knowledge-base",
        label: "Knowledge base",
        isTitle: false,
        icon: "book-open",
        permissions: ["admin_manage_knowledge_base"],
        url: "/content/knowledge-base",
    },
    {
        key: "ai-policies",
        label: "AI Policies",
        isTitle: false,
        icon: "message-square",
        permissions: ["admin_manage_ai_policies"],
        url: "/ai/policies",
    },
    {
        key: "ai-system-prompts",
        label: "AI System Prompts",
        isTitle: false,
        icon: "shield",
        permissions: ["super_admin_manage_ai_prompts"],
        url: "/ai/system-prompts",
    },
    {
        key: "parameters-support-auto-reply",
        label: "Support Auto-Reply",
        isTitle: false,
        icon: "message-square",
        permissions: ["see_ai_agent_config"],
        url: "/parameters/support-auto-reply",
    },

    // ─── CUSTOMERS ──────────────────────────────────────────────────
    {key: "customers-title", label: "Customers", isTitle: true},
    {
        key: "customer-customers",
        label: "Customers",
        isTitle: false,
        icon: "users",
        permissions: ["see_users"],
        url: "/customers/list",
    },
    {
        key: "customer-exclusive-referrals",
        label: "Exclusive referrals",
        isTitle: false,
        icon: "lock",
        permissions: ["admin_see_exclusive_referrals"],
        url: "/customers/exclusive-referrals",
    },
    {
        key: "ticket-tickets",
        label: "Tickets",
        isTitle: false,
        icon: "life-buoy",
        permissions: ["agent_ticket_support"],
        url: "/tickets/list",
    },
    {
        key: "ticket-ticket-categories",
        label: "Ticket Categories",
        isTitle: false,
        icon: "tag",
        permissions: ["admin_manage_ticket_categories"],
        url: "/tickets/categories",
    },

    // ─── SUPPLIERS ──────────────────────────────────────────────────
    {key: "suppliers-title", label: "Suppliers", isTitle: true},
    {
        key: "supplier-suppliers",
        label: "Suppliers",
        isTitle: false,
        icon: "briefcase",
        permissions: ["see_suppliers"],
        url: "/suppliers/list",
    },
    // Assigns platform payment methods to vendors (D2 — its dialog says "New
    // Assignment"). Distinct from the Finance "Payment Methods" catalogue.
    {
        key: "vendor-payment-methods",
        label: "Vendor Payment Methods",
        isTitle: false,
        icon: "credit-card",
        url: "/admin/vendor-payment-methods",
    },
    {
        key: "vendor-vendors",
        label: "Vendors",
        isTitle: false,
        icon: "briefcase",
        url: "/vendors/list",
    },

    // ─── KYB & COMPLIANCE ───────────────────────────────────────────
    // Cross-supplier review queues: applications, compliance documents, customer
    // contracts, and the per-service activation requests suppliers raise once live.
    {key: "compliance-title", label: "KYB & Compliance", isTitle: true},
    {
        key: "compliance",
        label: "KYB & Compliance",
        isTitle: false,
        icon: "shield",
        children: [
            { key: "waiting-approvals", label: "Supplier Applications", url: "/suppliers/waiting-approvals", parentKey: "compliance", permissions: ["see_suppliers"] },
            { key: "compliance-documents", label: "Document Approvals", url: "/compliance/documents", parentKey: "compliance", permissions: ["admin_review_compliance_document"] },
            { key: "compliance-contracts", label: "Signed Contracts", url: "/compliance/contracts", parentKey: "compliance", permissions: ["admin_see_contracts"] },
            // Moved here from the operations section (owner, 2026-10-15): it is a
            // review queue like its siblings, not a day-to-day operations screen.
            { key: "service-activation-requests", label: "Activation Requests", url: "/suppliers/activation-requests", parentKey: "compliance", permissions: ["admin_manage_service_availability"] },
        ],
    },

    // ─── TEAM ───────────────────────────────────────────────────────
    // D4 — staff records (Agents) and how staff get access (Users, Roles,
    // Permissions, MFA) were two sections; adding an agent meant visiting both.
    {key: "team-title", label: "Team", isTitle: true},
    {
        key: "agent-agents",
        label: "Agents",
        isTitle: false,
        icon: "user-check",
        permissions: ["see_users"],
        url: "/agents/list",
    },
    {
        key: "agent-agent-groups",
        label: "Agent Groups",
        isTitle: false,
        icon: "layers",
        permissions: ["admin_view_agent_groups"],
        url: "/agents/agent-groups",
    },
    {
        key: "authentication-users",
        label: "Users",
        isTitle: false,
        icon: "users",
        url: "/authentication/users",
    },
    {
        key: "authentication-user-types",
        label: "User Types",
        isTitle: false,
        icon: "shield",
        url: "/authentication/users-types",
    },
    {
        key: "authentication-roles",
        label: "Roles",
        isTitle: false,
        icon: "shield",
        url: "/authentication/roles",
    },
    {
        key: "authentication-permissions",
        label: "Permissions",
        isTitle: false,
        icon: "shield",
        url: "/authentication/permissions",
    },
    {
        key: "admin-mfa-management",
        label: "MFA Management",
        isTitle: false,
        icon: "shield",
        url: "/admin/mfa-management",
    },

    // ─── SYSTEM ─────────────────────────────────────────────────────
    // Not part of Team: Team is who has access (Users/Roles/Permissions/MFA),
    // this is what the running backend is configured with. Super admin only —
    // the same permission the API gates on, so the sidebar cannot advertise a
    // page that 403s.
    {key: "system-title", label: "System", isTitle: true},
    {
        key: "system-orphan-objects",
        label: "Orphaned Objects",
        isTitle: false,
        icon: "alert-triangle",
        permissions: ["admin_view_configuration"],
        url: "/admin/orphan-objects",
    },
    {
        key: "system-redis-cache",
        label: "Redis Cache",
        isTitle: false,
        icon: "layers",
        permissions: ["admin_list_users"],
        url: "/admin/redis-cache",
    },
    {
        key: "system-storage-objects",
        label: "Stored Objects",
        isTitle: false,
        icon: "database",
        permissions: ["admin_view_configuration"],
        url: "/admin/storage-objects",
    },
    {
        key: "system-configuration",
        label: "Environment Configuration",
        isTitle: false,
        icon: "sliders",
        permissions: ["admin_view_configuration"],
        url: "/admin/configuration",
    },
    {
        key: "system-messaging-channels",
        label: "Messaging channels",
        isTitle: false,
        icon: "message-square",
        permissions: ["admin_view_configuration"],
        url: "/admin/messaging-channels",
    },

    // ─── CONTENT ────────────────────────────────────────────────────
    {key: "content-title", label: "Content", isTitle: true},
    {
        key: "content",
        label: "Content",
        isTitle: false,
        icon: "grid",
        children: [
            { key: "recommended-content-main", label: "Recommended Content", url: "/recommended-content", parentKey: "content" },
            { key: "recommended-content-categories", label: "Categories", url: "/recommended-content/categories", parentKey: "content" },
            { key: "recommended-content-tags", label: "Tags", url: "/tags", parentKey: "content" },
            { key: "recommended-content-badges", label: "Badges", url: "/badges", parentKey: "content" },
            { key: "admin-public-assets", label: "Public Assets", url: "/admin/public-assets", parentKey: "content" },
            { key: "admin-promotions", label: "Promotions", url: "/admin/promotions", parentKey: "content", permissions: ["admin_manage_promotions"] },
            { key: "admin-promotion-settings", label: "Promotions settings", url: "/admin/promotions/settings", parentKey: "content", permissions: ["admin_manage_promotions"] },
            { key: "admin-cross-sell-settings", label: "Cross-sell settings", url: "/admin/cross-sell/settings", parentKey: "content", permissions: ["admin_manage_promotions"] },
        ],
    },
    {
        key: "notifications",
        label: "Notifications",
        isTitle: false,
        icon: "bell",
        children: [
            { key: "admin-notification-logs", label: "Notification Logs", url: "/admin/notification-logs", parentKey: "notifications" },
            { key: "admin-notification-settings", label: "Settings", url: "/admin/notification-settings", parentKey: "notifications" },
        ],
    },

    // ─── SYSTEM ─────────────────────────────────────────────────────
    // Dispatch and AI Agent moved out of here to Commercial Policy. The platform
    // documentation now lives in the knowledge base (Content → Knowledge base).
    {
        key: "system",
        label: "System",
        isTitle: false,
        icon: "package",
        children: [
            { key: "parameters-mobile-pages", label: "Mobile Pages", url: "/parameters/mobile-pages", parentKey: "system" },
            { key: "parameters-system-features", label: "System Features", url: "/system-features", parentKey: "system" },
            { key: "parameters-content-components", label: "Content Components", url: "/content-components", parentKey: "system" },
            { key: "parameters-content-component-templates", label: "Component Templates", url: "/content-component-templates", parentKey: "system" },
            { key: "system-storage-health", label: "Storage Health", url: "/storage-health", parentKey: "system" },
        ],
    },
    // NOTE: no "Account"/Profile nav group — Profile lives in the sidebar footer
    // (→ /account/profile), and the Profile page surfaces Change Password
    // (/account/change-password) and 2FA (/admin/security) from its Security card.

    // ─── REFERENCE DATA ─────────────────────────────────────────────
    // The lookup tables that emptied the "Parameters" drawer — edited rarely,
    // grouped once, out of the way.
    {
        key: "reference-data",
        label: "Reference Data",
        isTitle: false,
        icon: "sliders",
        children: [
            { key: "parameters-languages", label: "Languages", url: "/parameters/languages", parentKey: "reference-data" },
            { key: "parameters-allergens", label: "Allergens", url: "/parameters/allergens", parentKey: "reference-data" },
            { key: "parameters-transportation-types", label: "Transportation Types", url: "/parameters/transportation-types", parentKey: "reference-data" },
            { key: "parameters-places-countries", label: "Countries", url: "/parameters/places/countries", parentKey: "reference-data" },
            { key: "parameters-places-states", label: "States", url: "/parameters/places/states", parentKey: "reference-data" },
            { key: "parameters-places-cities", label: "Cities", url: "/parameters/places/cities", parentKey: "reference-data" },
            { key: "aviation-jet-classes", label: "Jet Classes", url: "/admin/jet-classes", parentKey: "reference-data" },
            { key: "aviation-airports", label: "Airports", url: "/admin/airports", parentKey: "reference-data" },
            { key: "yacht-types", label: "Yacht Types", url: "/admin/yacht-types", parentKey: "reference-data" },
            { key: "parameters-cars-brands", label: "Car Brands", url: "/parameters/cars/brands", parentKey: "reference-data" },
            { key: "parameters-cars-models", label: "Car Models", url: "/parameters/cars/models", parentKey: "reference-data" },
            { key: "event-categories", label: "Event Categories", url: "/events/event-categories", parentKey: "reference-data" },
            { key: "parameters-infos-info-groups", label: "Info Groups", url: "/parameters/infos/info-groups", parentKey: "reference-data" },
            { key: "parameters-infos-info-types", label: "Info Types", url: "/parameters/infos/info-types", parentKey: "reference-data" },
            { key: "parameters-likes-categories", label: "Like Categories", url: "/parameters/likes/categories", parentKey: "reference-data" },
            { key: "parameters-likes-lists", label: "Likes", url: "/parameters/likes", parentKey: "reference-data" },
        ],
    },
];

const HORIZONTAL_MENU_ITEMS: MenuItemTypes[] = [
    {
        key: "dashboard",
        icon: "home",
        label: "Dashboard",
        isTitle: true,
        children: [
            {
                key: "ds-dashboard-1",
                label: "Dashboard 1",
                url: "/dashboard-1",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-2",
                label: "Dashboard 2",
                url: "/dashboard-2",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-3",
                label: "Dashboard 3",
                url: "/dashboard-3",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-4",
                label: "Dashboard 4",
                url: "/dashboard-4",
                parentKey: "dashboard",
            },
        ],
    },
    {
        key: "apps",
        icon: "grid",
        label: "Apps",
        isTitle: true,
        children: [
            {
                key: "apps-calendar",
                label: "Calendar",
                isTitle: false,
                icon: "calendar",
                url: "/apps/calendar",
                parentKey: "apps",
            },
            {
                key: "apps-chat",
                label: "Chat",
                isTitle: false,
                icon: "message-square",
                url: "/apps/chat",
                parentKey: "apps",
            },
            {
                key: "apps-ecommerce",
                label: "Ecommerce",
                isTitle: false,
                icon: "shopping-cart",
                parentKey: "apps",
                children: [
                    {
                        key: "ecommerce-dashboard",
                        label: "Dashboard",
                        url: "/apps/ecommerce/dashboard",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-products",
                        label: "Products",
                        url: "/apps/ecommerce/products",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-details",
                        label: "Product Details",
                        url: "/apps/ecommerce/product-details",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-edit-product",
                        label: "Add Product",
                        url: "/apps/ecommerce/edit-product",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-customers",
                        label: "Customers",
                        url: "/apps/ecommerce/customers",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-orders",
                        label: "Orders",
                        url: "/apps/ecommerce/orders",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-order-details",
                        label: "Order Details",
                        url: "/apps/ecommerce/order/details",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-sellers",
                        label: "Sellers",
                        url: "/apps/ecommerce/sellers",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-shopping-cart",
                        label: "Shopping Cart",
                        url: "/apps/ecommerce/shopping-cart",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-checkout",
                        label: "Checkout",
                        url: "/apps/ecommerce/checkout",
                        parentKey: "apps-ecommerce",
                    },
                ],
            },
            {
                key: "apps-crm",
                label: "CRM",
                isTitle: false,
                icon: "users",
                parentKey: "apps",
                children: [
                    {
                        key: "crm-dashboard",
                        label: "Dashboard",
                        url: "/apps/crm/dashboard",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-contacts",
                        label: "Contacts",
                        url: "/apps/crm/contacts",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-opportunities",
                        label: "Opportunities",
                        url: "/apps/crm/opportunities",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-leads",
                        label: "Leads",
                        url: "/apps/crm/leads",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-customers",
                        label: "Customers",
                        url: "/apps/crm/customers",
                        parentKey: "apps-crm",
                    },
                ],
            },
            {
                key: "apps-email",
                label: "Email",
                isTitle: false,
                icon: "mail",
                parentKey: "apps",
                children: [
                    {
                        key: "email-inbox",
                        label: "Inbox",
                        url: "/apps/email/inbox",
                        parentKey: "apps-email",
                    },
                    {
                        key: "email-read-email",
                        label: "Read Email",
                        url: "/apps/email/details",
                        parentKey: "apps-email",
                    },
                    {
                        key: "email-compose-email",
                        label: "Compose Email",
                        url: "/apps/email/compose",
                        parentKey: "apps-email",
                    },
                ],
            },
            {
                key: "apps-social",
                label: "Social Feed",
                isTitle: false,
                icon: "rss",
                url: "/apps/social-feed",
                badge: {variant: "pink", text: "Hot"},
                parentKey: "apps",
            },
            {
                key: "apps-companies",
                label: "Companies",
                isTitle: false,
                icon: "activity",
                url: "/apps/companies",
                parentKey: "apps",
            },
            {
                key: "apps-projects",
                label: "Projects",
                isTitle: false,
                icon: "briefcase",
                parentKey: "apps",
                children: [
                    {
                        key: "project-list",
                        label: "List",
                        url: "/apps/projects/list",
                        parentKey: "apps-projects",
                    },
                    {
                        key: "project-details",
                        label: "Details",
                        url: "/apps/projects/:id/details",
                        parentKey: "apps-projects",
                    },
                    {
                        key: "project-create-project",
                        label: "Create Project",
                        url: "/apps/projects/create",
                        parentKey: "apps-projects",
                    },
                ],
            },
            {
                key: "apps-tasks",
                label: "Tasks",
                isTitle: false,
                icon: "clipboard",
                parentKey: "apps",
                children: [
                    {
                        key: "task-list",
                        label: "List",
                        url: "/apps/tasks/list",
                        parentKey: "apps-tasks",
                    },
                    {
                        key: "task-details",
                        label: "Details",
                        url: "/apps/tasks/details",
                        parentKey: "apps-tasks",
                    },
                    {
                        key: "task-kanban",
                        label: "Kanban Board",
                        url: "/apps/tasks/kanban",
                        parentKey: "apps-tasks",
                    },
                ],
            },
            {
                key: "apps-contacts",
                label: "Contacts",
                isTitle: false,
                icon: "book",
                parentKey: "apps",
                children: [
                    {
                        key: "contacts-list",
                        label: "Members List",
                        url: "/apps/contacts/list",
                        parentKey: "apps-contacts",
                    },
                    {
                        key: "contacts-profile",
                        label: "Profile",
                        url: "/apps/contacts/profile",
                        parentKey: "apps-contacts",
                    },
                ],
            },
            {
                key: "apps-tickets",
                label: "Tickets",
                isTitle: false,
                icon: "aperture",
                parentKey: "apps",
                children: [
                    {
                        key: "tickets-list",
                        label: "List",
                        url: "/apps/tickets/list",
                        parentKey: "apps-tickets",
                    },
                    {
                        key: "tickets-details",
                        label: "Details",
                        url: "/apps/tickets/details",
                        parentKey: "apps-tickets",
                    },
                ],
            },
            {
                key: "apps-file-manager",
                label: "File Manager",
                isTitle: false,
                icon: "folder-plus",
                url: "/apps/file-manager",
                parentKey: "apps",
            },
        ],
    },
    {
        key: "base-ui",
        icon: "briefcase",
        label: "UI Elements",
        isTitle: true,
        children: [
            {
                key: "base-ui-buttons",
                label: "Buttons",
                url: "/ui/buttons",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-cards",
                label: "Cards",
                url: "/ui/cards",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-avatars",
                label: "Avatars",
                url: "/ui/avatars",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-portlets",
                label: "Portlets",
                url: "/ui/portlets",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-tabs-accordions",
                label: "Tabs & Accordions",
                url: "/ui/tabs-accordions",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-modals",
                label: "Modals",
                url: "/ui/modals",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-progress",
                label: "Progress",
                url: "/ui/progress",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-notifications",
                label: "Notifications",
                url: "/ui/notifications",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-offcanvas",
                label: "Offcanvas",
                url: "/ui/offcanvas",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-placeholders",
                label: "Placeholders",
                url: "/ui/placeholders",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-spinners",
                label: "Spinners",
                url: "/ui/spinners",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-images",
                label: "Images",
                url: "/ui/images",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-carousel",
                label: "Carousel",
                url: "/ui/carousel",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-listgroups",
                label: "List Groups",
                url: "/ui/listgroups",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-embedvideo",
                label: "Embed Video",
                url: "/ui/embedvideo",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-dropdown",
                label: "Dropdowns",
                url: "/ui/dropdowns",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-ribbons",
                label: "Ribbons",
                url: "/ui/ribbons",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-tooltips-popovers",
                label: "Tooltips & Popovers",
                url: "/ui/tooltips-popovers",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-general",
                label: "General UI",
                url: "/ui/general",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-typography",
                label: "Typography",
                url: "/ui/typography",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-grid",
                label: "Grid",
                url: "/ui/grid",
                parentKey: "base-ui",
            },
        ],
    },
    {
        key: "components",
        icon: "package",
        label: "Components",
        isTitle: true,
        children: [
            {
                key: "extended-ui",
                label: "Extended UI",
                isTitle: false,
                icon: "layers",
                badge: {variant: "info", text: "Hot"},
                parentKey: "components",
                children: [
                    {
                        key: "extended-ui-nestable",
                        label: "Nestable List",
                        url: "/extended-ui/nestable",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-dragdrop",
                        label: "Drag and Drop",
                        url: "/extended-ui/dragdrop",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-rangesliders",
                        label: "Range Sliders",
                        url: "/extended-ui/rangesliders",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-animation",
                        label: "Animation",
                        url: "/extended-ui/animation",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-sweet-alert",
                        label: "Sweet Alert",
                        url: "/extended-ui/sweet-alert",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-tour",
                        label: "Tour Page",
                        url: "/extended-ui/tour",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-loading-buttons",
                        label: "Loading Buttons",
                        url: "/extended-ui/loading-buttons",
                        parentKey: "extended-ui",
                    },
                ],
            },
            {
                key: "widgets",
                label: "Widgets",
                isTitle: false,
                icon: "gift",
                url: "/ui/widgets",
                parentKey: "components",
            },
            {
                key: "icons",
                label: "Icons",
                isTitle: false,
                icon: "cpu",
                parentKey: "components",
                children: [
                    {
                        key: "icon-two-tone",
                        label: "Two Tone Icons",
                        url: "/ui/icons/two-tone",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-feather",
                        label: "Feather Icons",
                        url: "/ui/icons/feather",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-mdiicons",
                        label: "Material Design Icons",
                        url: "/ui/icons/mdi",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-dripicons",
                        label: "Dripicons",
                        url: "/ui/icons/dripicons",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-font-awesome",
                        label: "Font Awesome 5",
                        url: "/ui/icons/font-awesome",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-themify",
                        label: "Themify",
                        url: "/ui/icons/themify",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-simple-line",
                        label: "Simple Line",
                        url: "/ui/icons/simple-line",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-weather",
                        label: "Weather",
                        url: "/ui/icons/weather",
                        parentKey: "icons",
                    },
                ],
            },
            {
                key: "forms",
                label: "Forms",
                isTitle: false,
                icon: "bookmark",
                parentKey: "components",
                children: [
                    {
                        key: "form-basic",
                        label: "General Elements",
                        url: "/ui/forms/basic",
                        parentKey: "forms",
                    },
                    {
                        key: "form-advanced",
                        label: "Form Advanced",
                        url: "/ui/forms/advanced",
                        parentKey: "forms",
                    },
                    {
                        key: "form-validation",
                        label: "Validation",
                        url: "/ui/forms/validation",
                        parentKey: "forms",
                    },
                    {
                        key: "form-wizard",
                        label: "Wizard",
                        url: "/ui/forms/wizard",
                        parentKey: "forms",
                    },
                    {
                        key: "form-upload",
                        label: "File Uploads",
                        url: "/ui/forms/upload",
                        parentKey: "forms",
                    },
                    {
                        key: "form-editors",
                        label: "Editors",
                        url: "/ui/forms/editors",
                        parentKey: "forms",
                    },
                ],
            },
            {
                key: "tables",
                label: "Tables",
                isTitle: false,
                icon: "grid",
                parentKey: "components",
                children: [
                    {
                        key: "table-basic",
                        label: "Basic Tables",
                        url: "/ui/tables/basic",
                        parentKey: "tables",
                    },
                    {
                        key: "table-advanced",
                        label: "Advanced Tables",
                        url: "/ui/tables/advanced",
                        parentKey: "tables",
                    },
                ],
            },
            {
                key: "charts",
                label: "Charts",
                isTitle: false,
                parentKey: "components",
                icon: "bar-chart-2",
                children: [
                    {
                        key: "chart-apex",
                        label: "Apex Charts",
                        url: "/ui/charts/apex",
                        parentKey: "charts",
                    },
                    {
                        key: "chart-chartjs",
                        label: "Chartjs",
                        url: "/ui/charts/chartjs",
                        parentKey: "charts",
                    },
                ],
            },
            {
                key: "maps",
                label: "Maps",
                isTitle: false,
                icon: "map",
                parentKey: "components",
                children: [
                    {
                        key: "maps-googlemaps",
                        label: "Google Maps",
                        url: "/ui/googlemaps",
                        parentKey: "maps",
                    },
                    {
                        key: "maps-vectormaps",
                        label: "Vector Maps",
                        url: "/ui/vectormaps",
                        parentKey: "maps",
                    },
                ],
            },
            {
                key: "menu-levels",
                label: "Menu Levels",
                isTitle: false,
                icon: "share-2",
                parentKey: "components",
                children: [
                    {
                        key: "menu-levels-1-1",
                        label: "Level 1.1",
                        url: "/",
                        parentKey: "menu-levels",
                        children: [
                            {
                                key: "menu-levels-2-1",
                                label: "Level 2.1",
                                url: "/",
                                parentKey: "menu-levels-1-1",
                                children: [
                                    {
                                        key: "menu-levels-3-1",
                                        label: "Level 3.1",
                                        url: "/",
                                        parentKey: "menu-levels-2-1",
                                    },
                                    {
                                        key: "menu-levels-3-2",
                                        label: "Level 3.2",
                                        url: "/",
                                        parentKey: "menu-levels-2-1",
                                    },
                                ],
                            },
                            {
                                key: "menu-levels-2-2",
                                label: "Level 2.2",
                                url: "/",
                                parentKey: "menu-levels-1-1",
                            },
                        ],
                    },
                    {
                        key: "menu-levels-1-2",
                        label: "Level 1.2",
                        url: "/",
                        parentKey: "menu-levels",
                    },
                ],
            },
        ],
    },
    {
        key: "pages",
        icon: "file-text",
        label: "Pages",
        isTitle: true,
        children: [
            {
                key: "error-pages",
                label: "Errors",
                isTitle: false,
                parentKey: "pages",
                children: [
                    {
                        key: "page-error-404",
                        label: "Error - 404",
                        url: "/error-404",
                        parentKey: "error-pages",
                    },
                    {
                        key: "page-error-404-two",
                        label: "Error - 404 Two",
                        url: "/error-404-two",
                        parentKey: "error-pages",
                    },
                    {
                        key: "page-error-404-alt",
                        label: "Error - 404-alt",
                        url: "/pages/error-404-alt",
                        parentKey: "error-pages",
                    },
                    {
                        key: "page-error-500",
                        label: "Error - 500",
                        url: "/error-500",
                        parentKey: "error-pages",
                    },
                    {
                        key: "page-error-500-two",
                        label: "Error - 500 Two",
                        url: "/error-500-two",
                        parentKey: "error-pages",
                    },
                ],
            },
            {
                key: "extra-pages",
                label: "Utility",
                isTitle: false,
                parentKey: "pages",
                children: [
                    {
                        key: "page-starter",
                        label: "Starter",
                        url: "/pages/starter",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-timeline",
                        label: "Timeline",
                        url: "/pages/timeline",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-sitemap",
                        label: "Sitemap",
                        url: "/pages/sitemap",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-invoice",
                        label: "Invoice",
                        url: "/pages/invoice",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-faq",
                        label: "FAQs",
                        url: "/pages/faq",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-search-result",
                        label: "Search Results",
                        url: "/pages/serach-results",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-pricing",
                        label: "Pricing",
                        url: "/pages/pricing",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-maintenance",
                        label: "Maintenance",
                        url: "/maintenance",
                        target: "_blank",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-upcoming",
                        label: "Coming Soon",
                        url: "/upcoming",
                        parentKey: "extra-pages",
                    },
                    {
                        key: "page-gallery",
                        label: "Gallery",
                        url: "/pages/gallery",
                        parentKey: "extra-pages",
                    },
                ],
            },
        ],
    },
];

const TWO_COl_MENU_ITEMS: MenuItemTypes[] = [
    {
        key: "dashboard",
        icon: "home",
        label: "Dashboard",
        isTitle: true,
        children: [
            {
                key: "ds-dashboard-1",
                label: "Dashboard 1",
                url: "/dashboard-1",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-2",
                label: "Dashboard 2",
                url: "/dashboard-2",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-3",
                label: "Dashboard 3",
                url: "/dashboard-3",
                parentKey: "dashboard",
            },
            {
                key: "ds-dashboard-4",
                label: "Dashboard 4",
                url: "/dashboard-4",
                parentKey: "dashboard",
            },
        ],
    },
    {
        key: "apps",
        icon: "grid",
        label: "Apps",
        isTitle: true,
        children: [
            {
                key: "apps-calendar",
                label: "Calendar",
                isTitle: false,
                icon: "calendar",
                url: "/apps/calendar",
                parentKey: "apps",
            },
            {
                key: "apps-chat",
                label: "Chat",
                isTitle: false,
                icon: "message-square",
                url: "/apps/chat",
                parentKey: "apps",
            },
            {
                key: "apps-ecommerce",
                label: "Ecommerce",
                isTitle: false,
                icon: "shopping-cart",
                parentKey: "apps",
                children: [
                    {
                        key: "ecommerce-dashboard",
                        label: "Dashboard",
                        url: "/apps/ecommerce/dashboard",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-products",
                        label: "Products",
                        url: "/apps/ecommerce/products",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-details",
                        label: "Product Details",
                        url: "/apps/ecommerce/product-details",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-edit-product",
                        label: "Add Product",
                        url: "/apps/ecommerce/edit-product",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-customers",
                        label: "Customers",
                        url: "/apps/ecommerce/customers",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-orders",
                        label: "Orders",
                        url: "/apps/ecommerce/orders",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-order-details",
                        label: "Order Details",
                        url: "/apps/ecommerce/order/details",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-sellers",
                        label: "Sellers",
                        url: "/apps/ecommerce/sellers",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-shopping-cart",
                        label: "Shopping Cart",
                        url: "/apps/ecommerce/shopping-cart",
                        parentKey: "apps-ecommerce",
                    },
                    {
                        key: "ecommerce-checkout",
                        label: "Checkout",
                        url: "/apps/ecommerce/checkout",
                        parentKey: "apps-ecommerce",
                    },
                ],
            },
            {
                key: "apps-crm",
                label: "CRM",
                isTitle: false,
                icon: "users",
                parentKey: "apps",
                children: [
                    {
                        key: "crm-dashboard",
                        label: "Dashboard",
                        url: "/apps/crm/dashboard",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-contacts",
                        label: "Contacts",
                        url: "/apps/crm/contacts",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-opportunities",
                        label: "Opportunities",
                        url: "/apps/crm/opportunities",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-leads",
                        label: "Leads",
                        url: "/apps/crm/leads",
                        parentKey: "apps-crm",
                    },
                    {
                        key: "crm-customers",
                        label: "Customers",
                        url: "/apps/crm/customers",
                        parentKey: "apps-crm",
                    },
                ],
            },
            {
                key: "apps-email",
                label: "Email",
                isTitle: false,
                icon: "mail",
                parentKey: "apps",
                children: [
                    {
                        key: "email-inbox",
                        label: "Inbox",
                        url: "/apps/email/inbox",
                        parentKey: "apps-email",
                    },
                    {
                        key: "email-read-email",
                        label: "Read Email",
                        url: "/apps/email/details",
                        parentKey: "apps-email",
                    },
                    {
                        key: "email-compose-email",
                        label: "Compose Email",
                        url: "/apps/email/compose",
                        parentKey: "apps-email",
                    },
                ],
            },
            {
                key: "apps-social",
                label: "Social Feed",
                isTitle: false,
                icon: "rss",
                url: "/apps/social-feed",
                badge: {variant: "pink", text: "Hot"},
                parentKey: "apps",
            },
            {
                key: "apps-companies",
                label: "Companies",
                isTitle: false,
                icon: "activity",
                url: "/apps/companies",
                parentKey: "apps",
            },
            {
                key: "apps-projects",
                label: "Projects",
                isTitle: false,
                icon: "briefcase",
                parentKey: "apps",
                children: [
                    {
                        key: "project-list",
                        label: "List",
                        url: "/apps/projects/list",
                        parentKey: "apps-projects",
                    },
                    {
                        key: "project-details",
                        label: "Details",
                        url: "/apps/projects/:id/details",
                        parentKey: "apps-projects",
                    },
                    {
                        key: "project-create-project",
                        label: "Create Project",
                        url: "/apps/projects/create",
                        parentKey: "apps-projects",
                    },
                ],
            },
            {
                key: "apps-tasks",
                label: "Tasks",
                isTitle: false,
                icon: "clipboard",
                parentKey: "apps",
                children: [
                    {
                        key: "task-list",
                        label: "List",
                        url: "/apps/tasks/list",
                        parentKey: "apps-tasks",
                    },
                    {
                        key: "task-details",
                        label: "Details",
                        url: "/apps/tasks/details",
                        parentKey: "apps-tasks",
                    },
                    {
                        key: "task-kanban",
                        label: "Kanban Board",
                        url: "/apps/tasks/kanban",
                        parentKey: "apps-tasks",
                    },
                ],
            },
            {
                key: "apps-contacts",
                label: "Contacts",
                isTitle: false,
                icon: "book",
                parentKey: "apps",
                children: [
                    {
                        key: "contacts-list",
                        label: "Members List",
                        url: "/apps/contacts/list",
                        parentKey: "apps-contacts",
                    },
                    {
                        key: "contacts-profile",
                        label: "Profile",
                        url: "/apps/contacts/profile",
                        parentKey: "apps-contacts",
                    },
                ],
            },
            {
                key: "apps-tickets",
                label: "Tickets",
                isTitle: false,
                icon: "aperture",
                parentKey: "apps",
                children: [
                    {
                        key: "tickets-list",
                        label: "List",
                        url: "/apps/tickets/list",
                        parentKey: "apps-tickets",
                    },
                    {
                        key: "tickets-details",
                        label: "Details",
                        url: "/apps/tickets/details",
                        parentKey: "apps-tickets",
                    },
                ],
            },
            {
                key: "apps-file-manager",
                label: "File Manager",
                isTitle: false,
                icon: "folder-plus",
                url: "/apps/file-manager",
                parentKey: "apps",
            },
        ],
    },
    {
        key: "extra-pages",
        icon: "file-text",
        label: "Pages",
        isTitle: true,
        children: [
            {
                key: "page-starter",
                label: "Starter",
                url: "/pages/starter",
                parentKey: "extra-pages",
            },
            {
                key: "page-timeline",
                label: "Timeline",
                url: "/pages/timeline",
                parentKey: "extra-pages",
            },
            {
                key: "page-sitemap",
                label: "Sitemap",
                url: "/pages/sitemap",
                parentKey: "extra-pages",
            },
            {
                key: "page-invoice",
                label: "Invoice",
                url: "/pages/invoice",
                parentKey: "extra-pages",
            },
            {
                key: "page-faq",
                label: "FAQs",
                url: "/pages/faq",
                parentKey: "extra-pages",
            },
            {
                key: "page-search-result",
                label: "Search Results",
                url: "/pages/serach-results",
                parentKey: "extra-pages",
            },
            {
                key: "page-pricing",
                label: "Pricing",
                url: "/pages/pricing",
                parentKey: "extra-pages",
            },
            {
                key: "page-maintenance",
                label: "Maintenance",
                url: "/maintenance",
                target: "_blank",
                parentKey: "extra-pages",
            },
            {
                key: "page-upcoming",
                label: "Coming Soon",
                url: "/upcoming",
                parentKey: "extra-pages",
            },
            {
                key: "page-gallery",
                label: "Gallery",
                url: "/pages/gallery",
                parentKey: "extra-pages",
            },

            {
                key: "page-error-404",
                label: "Error - 404",
                url: "/error-404",
                parentKey: "extra-pages",
            },
            {
                key: "page-error-404-two",
                label: "Error - 404 Two",
                url: "/error-404-two",
                parentKey: "extra-pages",
            },
            {
                key: "page-error-404-alt",
                label: "Error - 404-alt",
                url: "/pages/error-404-alt",
                parentKey: "extra-pages",
            },
            {
                key: "page-error-500",
                label: "Error - 500",
                url: "/error-500",
                parentKey: "extra-pages",
            },
            {
                key: "page-error-500-two",
                label: "Error - 500 Two",
                url: "/error-500-two",
                parentKey: "extra-pages",
            },
        ],
    },
    {
        key: "base-ui",
        icon: "briefcase",
        label: "UI Elements",
        isTitle: true,
        children: [
            {
                key: "base-ui-buttons",
                label: "Buttons",
                url: "/ui/buttons",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-cards",
                label: "Cards",
                url: "/ui/cards",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-avatars",
                label: "Avatars",
                url: "/ui/avatars",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-portlets",
                label: "Portlets",
                url: "/ui/portlets",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-tabs-accordions",
                label: "Tabs & Accordions",
                url: "/ui/tabs-accordions",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-modals",
                label: "Modals",
                url: "/ui/modals",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-progress",
                label: "Progress",
                url: "/ui/progress",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-notifications",
                label: "Notifications",
                url: "/ui/notifications",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-offcanvas",
                label: "Offcanvas",
                url: "/ui/offcanvas",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-placeholders",
                label: "Placeholders",
                url: "/ui/placeholders",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-spinners",
                label: "Spinners",
                url: "/ui/spinners",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-images",
                label: "Images",
                url: "/ui/images",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-carousel",
                label: "Carousel",
                url: "/ui/carousel",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-listgroups",
                label: "List Groups",
                url: "/ui/listgroups",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-embedvideo",
                label: "Embed Video",
                url: "/ui/embedvideo",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-dropdown",
                label: "Dropdowns",
                url: "/ui/dropdowns",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-ribbons",
                label: "Ribbons",
                url: "/ui/ribbons",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-tooltips-popovers",
                label: "Tooltips & Popovers",
                url: "/ui/tooltips-popovers",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-general",
                label: "General UI",
                url: "/ui/general",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-typography",
                label: "Typography",
                url: "/ui/typography",
                parentKey: "base-ui",
            },
            {
                key: "base-ui-grid",
                label: "Grid",
                url: "/ui/grid",
                parentKey: "base-ui",
            },
        ],
    },
    {
        key: "components",
        icon: "package",
        label: "Components",
        isTitle: true,
        children: [
            {
                key: "extended-ui",
                label: "Extended UI",
                isTitle: false,
                icon: "layers",
                badge: {variant: "info", text: "Hot"},
                parentKey: "components",
                children: [
                    {
                        key: "extended-ui-nestable",
                        label: "Nestable List",
                        url: "/extended-ui/nestable",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-dragdrop",
                        label: "Drag and Drop",
                        url: "/extended-ui/dragdrop",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-rangesliders",
                        label: "Range Sliders",
                        url: "/extended-ui/rangesliders",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-animation",
                        label: "Animation",
                        url: "/extended-ui/animation",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-sweet-alert",
                        label: "Sweet Alert",
                        url: "/extended-ui/sweet-alert",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-tour",
                        label: "Tour Page",
                        url: "/extended-ui/tour",
                        parentKey: "extended-ui",
                    },
                    {
                        key: "extended-ui-loading-buttons",
                        label: "Loading Buttons",
                        url: "/extended-ui/loading-buttons",
                        parentKey: "extended-ui",
                    },
                ],
            },
            {
                key: "icons",
                label: "Icons",
                isTitle: false,
                icon: "cpu",
                parentKey: "components",
                children: [
                    {
                        key: "icon-two-tone",
                        label: "Two Tone Icons",
                        url: "/ui/icons/two-tone",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-feather",
                        label: "Feather Icons",
                        url: "/ui/icons/feather",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-mdiicons",
                        label: "Material Design Icons",
                        url: "/ui/icons/mdi",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-dripicons",
                        label: "Dripicons",
                        url: "/ui/icons/dripicons",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-font-awesome",
                        label: "Font Awesome 5",
                        url: "/ui/icons/font-awesome",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-themify",
                        label: "Themify",
                        url: "/ui/icons/themify",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-simple-line",
                        label: "Simple Line",
                        url: "/ui/icons/simple-line",
                        parentKey: "icons",
                    },
                    {
                        key: "icon-weather",
                        label: "Weather",
                        url: "/ui/icons/weather",
                        parentKey: "icons",
                    },
                ],
            },
            {
                key: "forms",
                label: "Forms",
                isTitle: false,
                icon: "bookmark",
                parentKey: "components",
                children: [
                    {
                        key: "form-basic",
                        label: "General Elements",
                        url: "/ui/forms/basic",
                        parentKey: "forms",
                    },
                    {
                        key: "form-advanced",
                        label: "Form Advanced",
                        url: "/ui/forms/advanced",
                        parentKey: "forms",
                    },
                    {
                        key: "form-validation",
                        label: "Validation",
                        url: "/ui/forms/validation",
                        parentKey: "forms",
                    },
                    {
                        key: "form-wizard",
                        label: "Wizard",
                        url: "/ui/forms/wizard",
                        parentKey: "forms",
                    },
                    {
                        key: "form-upload",
                        label: "File Uploads",
                        url: "/ui/forms/upload",
                        parentKey: "forms",
                    },
                    {
                        key: "form-editors",
                        label: "Editors",
                        url: "/ui/forms/editors",
                        parentKey: "forms",
                    },
                ],
            },
            {
                key: "tables",
                label: "Tables",
                isTitle: false,
                icon: "grid",
                parentKey: "components",
                children: [
                    {
                        key: "table-basic",
                        label: "Basic Tables",
                        url: "/ui/tables/basic",
                        parentKey: "tables",
                    },
                    {
                        key: "table-advanced",
                        label: "Advanced Tables",
                        url: "/ui/tables/advanced",
                        parentKey: "tables",
                    },
                ],
            },
            {
                key: "charts",
                label: "Charts",
                isTitle: false,
                parentKey: "components",
                icon: "bar-chart-2",
                children: [
                    {
                        key: "chart-apex",
                        label: "Apex Charts",
                        url: "/ui/charts/apex",
                        parentKey: "charts",
                    },
                    {
                        key: "chart-chartjs",
                        label: "Chartjs",
                        url: "/ui/charts/chartjs",
                        parentKey: "charts",
                    },
                ],
            },
            {
                key: "maps",
                label: "Maps",
                isTitle: false,
                icon: "map",
                parentKey: "components",
                children: [
                    {
                        key: "maps-googlemaps",
                        label: "Google Maps",
                        url: "/ui/googlemaps",
                        parentKey: "maps",
                    },
                    {
                        key: "maps-vectormaps",
                        label: "Vector Maps",
                        url: "/ui/vectormaps",
                        parentKey: "maps",
                    },
                ],
            },
            {
                key: "menu-levels",
                label: "Menu Levels",
                isTitle: false,
                icon: "share-2",
                parentKey: "components",
                children: [
                    {
                        key: "menu-levels-1-1",
                        label: "Level 1.1",
                        url: "/",
                        parentKey: "menu-levels",
                        children: [
                            {
                                key: "menu-levels-2-1",
                                label: "Level 2.1",
                                url: "/",
                                parentKey: "menu-levels-1-1",
                                children: [
                                    {
                                        key: "menu-levels-3-1",
                                        label: "Level 3.1",
                                        url: "/",
                                        parentKey: "menu-levels-2-1",
                                    },
                                    {
                                        key: "menu-levels-3-2",
                                        label: "Level 3.2",
                                        url: "/",
                                        parentKey: "menu-levels-2-1",
                                    },
                                ],
                            },
                            {
                                key: "menu-levels-2-2",
                                label: "Level 2.2",
                                url: "/",
                                parentKey: "menu-levels-1-1",
                            },
                        ],
                    },
                    {
                        key: "menu-levels-1-2",
                        label: "Level 1.2",
                        url: "/",
                        parentKey: "menu-levels",
                    },
                ],
            },
        ],
    },
    {key: "aviation", label: "Aviation", isTitle: true},
    {
        key: "aviation-jet-classes",
        label: "Jet Classes",
        isTitle: false,
        icon: "layers",
        permissions: ["admin_see_jet_classes"],
        url: "/admin/jet-classes",
    },
    {
        key: "aviation-airports",
        label: "Airports",
        isTitle: false,
        icon: "route",
        permissions: ["admin_see_airports"],
        url: "/admin/airports",
    },
    {
        key: "aviation-aircrafts",
        label: "Aircrafts",
        isTitle: false,
        icon: "airplay",
        permissions: ["admin_see_aircrafts"],
        url: "/admin/aircrafts",
    },
    {
        key: "aviation-empty-legs",
        label: "Empty Legs",
        isTitle: false,
        icon: "slash",
        permissions: ["admin_see_empty_legs"],
        url: "/admin/empty-legs",
    },
    {
        isTitle: true,
        key: "widgets",
        label: "Other page",
        icon: "gift",
        url: "/ui/widgets",
        children: [
            {
                key: "widgets1",
                label: "Widgets",
                url: "/ui/widgets",
                parentKey: "widgets",
            },
        ],
    },
    // {
    //     key: 'widgets',
    //     label: 'Widgets',
    //     isTitle: false,
    //     icon: 'gift',
    //     url: '/ui/widgets',
    // },
];

export {MENU_ITEMS, TWO_COl_MENU_ITEMS, HORIZONTAL_MENU_ITEMS};
